import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";

interface PendingPayment {
  id: string;
  amount: number;
  final_amount: number | null;
  currency: string;
  status: string;
  created_at: string;
  course_id: string | null;
  courses: { title: string; slug: string | null } | null;
}

interface ExpiringEnrollment {
  id: string;
  access_expires_at: string;
  courses: { title: string; slug: string | null } | null;
}

const DISMISS_KEY = "pending-payment-banner-dismissed-at";
const EXPIRY_DISMISS_KEY = "expiring-access-banner-dismissed-at";

/**
 * Lightweight global banner shown on authenticated dashboard pages whenever
 * the current user has either:
 *  - a pending or overdue payment (`payment_history`), OR
 *  - a duration-based enrollment expiring within 7 days (`enrollments`).
 *
 * Pending payment takes precedence (more urgent: blocks renewal of existing
 * access). Both variants honor a 24h local dismissal so the user isn't nagged.
 */
export function PendingPaymentBanner() {
  const { user } = useAuth();
  const [pending, setPending] = useState<PendingPayment | null>(null);
  const [expiring, setExpiring] = useState<ExpiringEnrollment | null>(null);
  const [hidden, setHidden] = useState(true);
  const [expiringHidden, setExpiringHidden] = useState(true);

  useEffect(() => {
    if (!user?.id) return;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const dismissedRecently = Date.now() - dismissedAt < 24 * 60 * 60 * 1000;
    const expDismissedAt = Number(localStorage.getItem(EXPIRY_DISMISS_KEY) || 0);
    const expDismissedRecently = Date.now() - expDismissedAt < 24 * 60 * 60 * 1000;

    let cancelled = false;
    (async () => {
      // Pending payment
      const { data: pay } = await supabase
        .from("payment_history")
        .select("id, amount, final_amount, currency, status, created_at, course_id, courses(title, slug)")
        .eq("user_id", user.id)
        .in("status", ["pending", "overdue", "failed"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      // Expiring access (next 7 days, lifetime ones excluded by NOT NULL)
      const dayMs = 86_400_000;
      const fromIso = new Date(Date.now() - 2 * dayMs).toISOString(); // include grace
      const toIso = new Date(Date.now() + 7 * dayMs).toISOString();
      const { data: enr } = await supabase
        .from("enrollments")
        .select("id, access_expires_at, courses(title, slug)")
        .eq("user_id", user.id)
        .not("access_expires_at", "is", null)
        .gte("access_expires_at", fromIso)
        .lte("access_expires_at", toIso)
        .order("access_expires_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;
      if (pay) {
        setPending(pay as unknown as PendingPayment);
        setHidden(dismissedRecently);
      }
      if (enr) {
        setExpiring(enr as unknown as ExpiringEnrollment);
        setExpiringHidden(expDismissedRecently);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Pending payment wins.
  if (pending && !hidden) {
    const amount = Number(pending.final_amount ?? pending.amount).toFixed(2);
    const isOverdue = pending.status === "overdue" || pending.status === "failed";
    const courseTitle = pending.courses?.title;
    const payHref = pending.courses?.slug ? `/courses/${pending.courses.slug}?renew=1` : "/billing";
    return (
      <div
        role="alert"
        className={`mb-4 flex items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
          isOverdue
            ? "border-destructive/40 bg-destructive/10 text-destructive-foreground"
            : "border-amber-500/40 bg-amber-500/10"
        }`}
      >
        <div className="flex items-center gap-3 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            {isOverdue ? "Pagamento em atraso" : "Pagamento pendente"}
            {courseTitle ? <> de <strong>"{courseTitle}"</strong></> : null}
            {" — "}
            <strong>
              {pending.currency} {amount}
            </strong>
            . Regularize para manter seu acesso ativo.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant={isOverdue ? "destructive" : "default"}>
            <Link to={payHref}>Pagar agora</Link>
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Dispensar"
            onClick={() => {
              localStorage.setItem(DISMISS_KEY, String(Date.now()));
              setHidden(true);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Otherwise show expiring-access reminder if any.
  if (expiring && !expiringHidden) {
    const expMs = new Date(expiring.access_expires_at).getTime();
    const dayMs = 86_400_000;
    const diffMs = expMs - Date.now();
    const inGrace = diffMs <= 0;
    const days = Math.ceil(diffMs / dayMs);
    const courseTitle = expiring.courses?.title ?? "seu curso";
    const renewHref = expiring.courses?.slug
      ? `/courses/${expiring.courses.slug}?renew=1`
      : "/my-courses?tab=expired";

    let message: string;
    if (inGrace) {
      const graceLeft = Math.max(0, Math.ceil((expMs + 2 * dayMs - Date.now()) / dayMs));
      message =
        graceLeft > 0
          ? `Acesso a "${courseTitle}" expirou — ${graceLeft} ${graceLeft === 1 ? "dia" : "dias"} de cortesia restantes.`
          : `Acesso a "${courseTitle}" foi bloqueado.`;
    } else if (days <= 1) {
      message = `Acesso a "${courseTitle}" expira em menos de 24 horas.`;
    } else {
      message = `Acesso a "${courseTitle}" expira em ${days} dias.`;
    }

    const tone = inGrace || days <= 1
      ? "border-destructive/40 bg-destructive/10 text-destructive-foreground"
      : "border-amber-500/40 bg-amber-500/10";

    return (
      <div
        role="alert"
        className={`mb-4 flex items-center justify-between gap-3 rounded-lg border px-4 py-3 ${tone}`}
      >
        <div className="flex items-center gap-3 text-sm min-w-0">
          {inGrace ? (
            <AlertTriangle className="h-4 w-4 shrink-0" />
          ) : (
            <Clock className="h-4 w-4 shrink-0" />
          )}
          <span className="truncate">{message}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button asChild size="sm" variant={inGrace || days <= 1 ? "destructive" : "default"}>
            <Link to={renewHref}>Renovar</Link>
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Dispensar"
            onClick={() => {
              localStorage.setItem(EXPIRY_DISMISS_KEY, String(Date.now()));
              setExpiringHidden(true);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
